import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { Field } from '../ui/Field'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/**
 * Kiturile de sampling: combinații de produse gata configurate.
 *
 * Rostul lor e să scoată munca repetată din teren — „ce dăm în farmacii" e o decizie de
 * marketing luată o dată, nu la fiecare vizită. Agentul alege kitul dintr-o atingere.
 *
 * La crearea acțiunii liniile se COPIAZĂ. Un kit modificat mâine nu rescrie ce s-a dat luna
 * trecută; altfel procesele-verbale emise ar începe să contrazică kitul din care au ieșit.
 */

export interface Kit {
  id: number
  denumire: string
  tip: string
  activ: boolean
  linii: { productId: number; sku: string; denumire: string; cantitate: number }[]
}

interface ProdusCatalog {
  id: number
  sku: string
  name: string
  /** Deja folosit la sampling. Vine de la server, sortat primele. */
  esteMostra?: boolean
}

export const TIP_KIT_LABEL: Record<string, string> = {
  orice: 'Oriunde',
  farmacie: 'Farmacii',
  eveniment: 'Evenimente',
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

export function TabKituri({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [editare, setEditare] = useState<Kit | 'nou' | null>(null)

  const kituri = useQuery({
    queryKey: ['sampling', 'kituri'],
    queryFn: async () => (await api.get<Kit[]>('/marketing/sampling/kituri')).data,
  })

  const comuta = useMutation({
    mutationFn: async (k: Kit) =>
      api.patch(`/marketing/sampling/kituri/${k.id}`, { activ: !k.activ }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'kituri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={() => setEditare('nou')}>Kit nou</Button>
        </div>
      )}

      <Card>
        {kituri.isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !kituri.data?.length ? (
          <p className="p-4 text-sm text-b4l-text-muted">
            Niciun kit. Fă unul și îl alegi dintr-o atingere la fiecare acțiune, în loc să bifezi
            produsele de fiecare dată.
          </p>
        ) : (
          <div className="space-y-2 p-3">
            {/*
              Fiecare kit intr-un chenar al lui. Inainte erau randuri lipite, despartite doar de o
              linie subtire, iar continutul curgea in pastile una langa alta — la un kit de sase
              componente cu denumiri lungi nu se mai vedea unde se termina unul si incepe celalalt.
            */}
            {kituri.data.map((k) => (
              <div key={k.id} className="rounded-lg border border-b4l-border bg-white p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${k.activ ? 'text-b4l-text' : 'text-b4l-text-dim'}`}
                      >
                        {k.denumire}
                      </span>
                      <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">
                        {TIP_KIT_LABEL[k.tip] ?? k.tip}
                      </span>
                      {!k.activ && (
                        <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-dim">
                          inactiv
                        </span>
                      )}
                    </div>
                    {/*
                    Componentele una sub alta, cu cantitatea aliniata la dreapta. In pastile
                    insirate nu se putea urmari ce contine kitul; asa se citeste ca o lista de
                    ambalat, care e chiar ce face omul cu ea.
                  */}
                    <ul className="mt-2 divide-y divide-b4l-border rounded-lg bg-b4l-bg">
                      {k.linii.map((l) => (
                        <li
                          key={l.productId}
                          className="flex items-baseline justify-between gap-3 px-2.5 py-1.5 text-sm"
                        >
                          <span className="text-b4l-text">{l.denumire}</span>
                          <span className="shrink-0 font-mono text-xs text-b4l-text-muted">
                            ×{l.cantitate}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 gap-3">
                      <button
                        onClick={() => setEditare(k)}
                        className="text-xs text-b4l-blue hover:underline"
                      >
                        Editează
                      </button>
                      <button
                        onClick={() => comuta.mutate(k)}
                        className="text-xs text-b4l-text-muted hover:underline"
                      >
                        {k.activ ? 'Dezactivează' : 'Activează'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editare && (
        <ModalKit kit={editare === 'nou' ? null : editare} onClose={() => setEditare(null)} />
      )}
    </div>
  )
}

function ModalKit({ kit, onClose }: { kit: Kit | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [denumire, setDenumire] = useState(kit?.denumire ?? '')
  const [tip, setTip] = useState(kit?.tip ?? 'orice')
  /** Cautare in lista de produse. Fara ea, tot raftul de suplimente se parcurge cu ochiul. */
  const [cautaProdus, setCautaProdus] = useState('')
  const [selectie, setSelectie] = useState<Record<number, number>>(
    Object.fromEntries((kit?.linii ?? []).map((l) => [l.productId, l.cantitate])),
  )

  const catalog = useQuery({
    queryKey: ['sampling', 'produse'],
    queryFn: async () =>
      (await api.get<{ produse: ProdusCatalog[] }>('/marketing/sampling/produse')).data,
  })

  const salveaza = useMutation({
    mutationFn: async () => {
      const linii = Object.entries(selectie).map(([pid, cant]) => ({
        productId: Number(pid),
        cantitate: cant,
      }))
      const body = { denumire: denumire.trim(), tip, linii }
      return kit
        ? api.patch(`/marketing/sampling/kituri/${kit.id}`, body)
        : api.post('/marketing/sampling/kituri', body)
    },
    onSuccess: () => {
      toast.success(kit ? 'Kit actualizat' : 'Kit creat')
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'kituri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const nrSelectate = Object.keys(selectie).length

  return (
    <Modal open onClose={onClose} title={kit ? `Kit: ${kit.denumire}` : 'Kit nou'}>
      <div className="space-y-3">
        <Field
          label="Denumire"
          value={denumire}
          onChange={(e) => setDenumire(e.target.value)}
          placeholder="Ex.: Kit Standard Farmacie"
          hint="Cum îl recunoaște agentul când îl alege."
        />
        <label className="block">
          <span className="text-sm font-medium text-b4l-text">Unde se potrivește</span>
          <select
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
          >
            <option value="orice">Oriunde</option>
            <option value="farmacie">Farmacii</option>
            <option value="eveniment">Evenimente</option>
          </select>
          <span className="mt-1 block text-xs text-b4l-text-muted">
            Doar o sugestie la alegere — nu blochează nimic.
          </span>
        </label>

        <div>
          <p className="text-sm font-medium text-b4l-text">
            Ce conține {nrSelectate > 0 && `(${nrSelectate} produse)`}
          </p>
          {/*
            Cautare, ca la selectorul de produse al actiunii. Lista tine acum si suplimentele, si
            materialele promotionale. Ce e deja bifat ramane vizibil oricare ar fi cautarea —
            altfel dispare de sub mana omului taman cand verifica ce a ales.
          */}
          {(catalog.data?.produse.length ?? 0) > 8 && (
            <input
              value={cautaProdus}
              onChange={(e) => setCautaProdus(e.target.value)}
              placeholder="Caută după denumire sau cod"
              className="mt-2 w-full rounded-lg border border-b4l-border px-3 py-1.5 text-sm"
            />
          )}
          {!catalog.data?.produse.length ? (
            <p className="mt-2 text-sm text-b4l-amber-dark">
              Catalogul nu are produse disponibile. Adaugă-le din Setări → Catalog produse.
            </p>
          ) : (
            <div className="mt-2 space-y-2">
              {catalog.data.produse
                .filter((p) => {
                  const t = cautaProdus.trim().toLowerCase()
                  if (!t) return true
                  if (p.id in selectie) return true
                  return p.name.toLowerCase().includes(t) || p.sku.toLowerCase().includes(t)
                })
                .map((p) => {
                  const ales = p.id in selectie
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={ales}
                        onChange={(e) =>
                          setSelectie((s) => {
                            const copie = { ...s }
                            if (e.target.checked) copie[p.id] = s[p.id] ?? 1
                            else delete copie[p.id]
                            return copie
                          })
                        }
                        className="h-4 w-4"
                      />
                      <span className="flex-1">
                        {p.name}
                        <span className="ml-1 text-xs text-b4l-text-dim">{p.sku}</span>
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={selectie[p.id] ?? 1}
                        disabled={!ales}
                        onChange={(e) =>
                          setSelectie((s) => ({
                            ...s,
                            [p.id]: Math.max(1, Number(e.target.value) || 1),
                          }))
                        }
                        className="w-16 rounded border border-b4l-border px-2 py-1 text-sm disabled:opacity-40"
                      />
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Anulează
        </Button>
        <Button
          disabled={denumire.trim().length < 3 || nrSelectate === 0 || salveaza.isPending}
          onClick={() => salveaza.mutate()}
        >
          {salveaza.isPending ? 'Se salvează…' : 'Salvează'}
        </Button>
      </div>
    </Modal>
  )
}
